import { Alert } from "@mui/material";
import * as React from "react";

import {
  ThreeDPanelState,
  usePanelState,
  useVizDispatch,
} from "@/shared/state/visualization";
import { setThreeDModel } from "@/shared/state/visualization/actions";
import type { EntityID } from "@/shared/state/visualization/schema/v1/three-d";

import { ModelFilePicker, type PickedModel } from "./ModelFilePicker";
import { planModelRepick } from "./modelRepick";

interface ModelImportControlProps {
  /** The entity whose mesh will name the chosen file. */
  entityId: EntityID;
  panelId: string;
  /** Whether picking an unconverted file may start a conversion. */
  canIngest: boolean;
  /** Called once the model is on the entity, so the caller can dismiss this. */
  onImported: () => void;
  /** Called when the pick was abandoned or its error dismissed. */
  onCancelled: () => void;
}

/**
 * Picking a model file and pointing one entity at it.
 *
 * Split out of `MeshConfig` and mounted only while a model is being chosen,
 * because the picker depends on the domain services and on org context.
 * Rendering a sphere's radius should not. Keeping that dependency inside this
 * component means the mesh form stays a plain set of controls that any tree can
 * render.
 *
 * Choosing a model only commits it. The panel fetches and parses the file as
 * part of the model mesh's load, which is also where the file's joints are read
 * and its declared default bindings applied. So nothing here waits on the
 * file: the panel's loading indicator covers the load, and its model error
 * alert reports a file that cannot be loaded.
 */
export function ModelImportControl({
  entityId,
  panelId,
  canIngest,
  onImported,
  onCancelled,
}: ModelImportControlProps) {
  const dispatch = useVizDispatch();
  const panelState = usePanelState(panelId) as ThreeDPanelState;
  const [picking, setPicking] = React.useState(true);
  /** Why a pick could not be turned into a model, when that is what went wrong. */
  const [pickError, setPickError] = React.useState<string | undefined>();
  /** Set once a file is chosen, so a trailing close is not read as a cancel. */
  const submittedRef = React.useRef(false);

  // Checked before committing, so an entity deleted while the picker was open
  // is caught rather than given a mesh nothing draws. The fallback covers a
  // workspace saved before the registry existed.
  const registry = React.useMemo(
    () =>
      panelState.config?.entities ??
      Object.fromEntries(
        Object.keys(panelState.data.entities).map((id) => [id, {}]),
      ),
    [panelState.config?.entities, panelState.data.entities],
  );

  /** The model this entity draws today, if it draws one. */
  const currentModel = React.useMemo(() => {
    const mesh = panelState.data.entities[entityId]?.mesh;
    return mesh?.type === "model" ? mesh : undefined;
  }, [panelState.data.entities, entityId]);

  const onSubmit = React.useCallback(
    (picked: PickedModel) => {
      setPicking(false);
      submittedRef.current = true;

      // Re-picking is a real gesture now that the model field opens this, so
      // what a pick means depends on what the entity already draws — including
      // "nothing at all", when the same model is chosen again.
      const plan = planModelRepick(currentModel, picked);
      if (plan.action === "keep") {
        onImported();
        return;
      }
      if (!registry[entityId]) {
        setPickError("That entity is no longer in this panel");
        return;
      }

      // `source` is always here now — the picker offers only robot
      // descriptions, and every one resolves to an artifact. The action still
      // takes it optionally, because the mesh schema keeps it optional for
      // workspaces saved when a plain glTF could be picked.
      dispatch(
        setThreeDModel(panelId, {
          entityId,
          file: picked.model,
          source: picked.source,
          ...(plan.jointValues ? { jointValues: plan.jointValues } : {}),
        }),
      );
      onImported();
    },
    [currentModel, dispatch, entityId, onImported, panelId, registry],
  );

  const onPickFailed = React.useCallback((message: string) => {
    setPicking(false);
    submittedRef.current = true;
    setPickError(message);
  }, []);

  const onClose = React.useCallback(() => {
    setPicking(false);
    // The browser closes itself after a pick as well as after a dismissal, and
    // the two must not be confused: reporting a cancel once a model has been
    // chosen would tell the caller to revert a choice that was just committed.
    if (!submittedRef.current) {
      onCancelled();
    }
  }, [onCancelled]);

  return (
    <>
      {picking && (
        <ModelFilePicker
          entityId={entityId}
          canIngest={canIngest}
          onClose={onClose}
          onSubmit={onSubmit}
          onError={onPickFailed}
        />
      )}

      {pickError !== undefined && (
        <Alert
          severity="error"
          onClose={() => {
            setPickError(undefined);
            onCancelled();
          }}
        >
          {pickError}
        </Alert>
      )}
    </>
  );
}
